import { useState, useEffect, useCallback, useContext } from "react";
import Context from "../../../scripts/context/User";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { useOutletContext } from "react-router-dom";

import { CircularProgress, IconButton } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ReactComponent as PdfIcon } from "../../../assets/pdf.svg";
import Modal from "@mui/material/Modal";

import { ModalStyle } from "./style";

const ACEPTED_FILE_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "application/pdf",
];

export default function UploadKYC() {
  const { globalState, setGlobalState } = useContext(Context);
  const {
    data,
    setData,
    handleNext,
    errorsFromAPI,
    setErrorsFromAPI,
    setLoading,
  } = useOutletContext();
  const [openPreview, setOpenPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [gettingFile, setGettingFile] = useState(false);

  const getFile = async () => {
    setGettingFile(true);
    const { data: apiData } = await axios.get("register/KYC/getKYC");

    if (apiData.document_type === "pdf") {
      axios
        .get(apiData.document, {
          responseType: "blob",
        })
        .then((response) => {
          const file = new Blob([response.data], { type: "application/pdf" });
          const fileURL = URL.createObjectURL(file);
          setData({ kyc_file: fileURL, type: "pdf" });
          setGettingFile(false);
          setUploading(false);
        });
    } else {
      setData({ kyc_file: apiData.document, type: "image" });
      setGettingFile(false);
      setUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file.size > 5242880) {
      return toast.error("File size is too big");
    }

    if (ACEPTED_FILE_TYPES.includes(file.type)) {
      const formData = new FormData();
      formData.append("government_document", file);

      setUploading(true);
      setLoading(true);
      axios
        .post("register/KYC/uploadKYC", formData, {
          headers: {
            "content-Type": "multipart/form-data",
          },
        })
        .then(({ data }) => {
          if (data.document_type === "pdf") {
            axios.get(data.document).then((response) => {
              const file = new Blob([response.data], {
                type: "application/pdf",
              });
              const fileURL = URL.createObjectURL(file);

              setData({ kyc_file: fileURL, type: "pdf" });
              setLoading(false);
            });
          } else {
            setData({ kyc_file: data.document, type: "image" });
            setLoading(false);
          }
        })
        .catch(({ response }) => {
          toast.error(response.data.errors.government_document[0]);
          setUploading(false);
          setLoading(false);
        });
    } else {
      toast.error(
        "The government document must be a file of type: jpg, jpeg, png, pdf."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*, application/pdf",
    multiple: false,
  });

  useEffect(() => {
    getFile();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => setOpenPreview(false);
  const openModal = () => setOpenPreview(true);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!data.kyc_file)
      return toast.error("Please upload a KYC document before proceeding.");

    setLoading(true);
    axios
      .post("/register/KYC/confirmKYC")
      .then(() => {
        setLoading(false);
        setGlobalState({
          ...globalState,
          registration_step:
            globalState.registration_step > 4
              ? globalState.registration_step
              : 4,
        });
        handleNext();
        setErrorsFromAPI("");
      })
      .catch(({ response }) => {
        setLoading(false);
        toast.error(response.data.message);
      });
  };

  const kyc_image = data.kyc_file;

  return (
    <div className="pb-5">
      {openPreview && (
        <Modal
          aria-labelledby="image-preview"
          aria-describedby="image-preview"
          open={openPreview}
          onClose={handleClose}
        >
          <ModalStyle onClick={handleClose}>
            <img src={kyc_image} alt="preview" />
          </ModalStyle>
        </Modal>
      )}
      <div className="pt-lg-0 pt-5 pb-15">
        <h3 className="font-weight-bolder text-white font-size-h2 font-size-h1-lg">
          Upload KYC
        </h3>
        <div className="text-muted font-weight-bold font-size-h4">
          Please upload a government-issued document
          <br />
          (such as a passport or driving license) for verification. We accept
          png, jpeg, jpg, and pdf formats.
        </div>
      </div>

      {errorsFromAPI && (
        <div className="d-flex align-items-center bg-light-danger rounded p-5 mb-9 status-div">
          <span className="svg-icon svg-icon-danger mr-5">
            <span className="svg-icon svg-icon-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24px"
                height="24px"
                viewBox="0 0 24 24"
                version="1.1"
              >
                <g
                  stroke="none"
                  stroke-width="1"
                  fill="none"
                  fill-rule="evenodd"
                >
                  <rect x="0" y="0" width="24" height="24"></rect>
                  <path
                    d="M11.1669899,4.49941818 L2.82535718,19.5143571 C2.557144,19.9971408 2.7310878,20.6059441 3.21387153,20.8741573 C3.36242953,20.9566895 3.52957021,21 3.69951446,21 L21.2169432,21 C21.7692279,21 22.2169432,20.5522847 22.2169432,20 C22.2169432,19.8159952 22.1661743,19.6355579 22.070225,19.47855 L12.894429,4.4636111 C12.6064401,3.99235656 11.9909517,3.84379039 11.5196972,4.13177928 C11.3723594,4.22181902 11.2508468,4.34847583 11.1669899,4.49941818 Z"
                    fill="#000000"
                    opacity="0.3"
                  ></path>
                  <rect
                    fill="#000000"
                    x="11"
                    y="9"
                    width="2"
                    height="7"
                    rx="1"
                  ></rect>
                  <rect
                    fill="#000000"
                    x="11"
                    y="17"
                    width="2"
                    height="2"
                    rx="1"
                  ></rect>
                </g>
              </svg>
            </span>
          </span>
          <div className="d-flex flex-column flex-grow-1 mr-2">
            <span
              href="#"
              className="text-danger py-1 font-size-lg font-size-status"
            >
              Rejected
            </span>
            <p className="text-white fw-bold d-block text-break">
              {errorsFromAPI
                .replace(/(\r\n|\n|\r)/gm, " ")
                .split(" ")
                .map((word, index) => (
                  <span key={index}>
                    {word}
                    <br />
                  </span>
                ))}
            </p>
          </div>
        </div>
      )}

      <form id="UploadKYC" onSubmit={handleSubmit}>
        <div className="MuiBox-root css-73huve">
          <div className="sc-jrQzAO ggWEKS pb-15">
            {gettingFile ? (
              <div
                style={{
                  height: "15vh",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress
                  sx={{
                    color: "#fff",
                  }}
                />
              </div>
            ) : data.kyc_file ? (
              data.type === "pdf" ? (
                <div className="position-relative fit-content-width mx-auto">
                  <IconButton
                    color="primary"
                    aria-label="upload kyc"
                    component="div"
                    className="image-preview-settings"
                    onClick={() => {
                      window.open(data.kyc_file);
                    }}
                    sx={{
                      color: "#a01c1c",
                      width: errorsFromAPI ? "fit-content" : "100px",
                      height: "100px",
                      marginBottom: "0.5rem",
                      svg: {
                        width: "80px",
                        height: "80px",
                      },
                      a: {
                        color: "unset",
                      },
                    }}
                  >
                    <PdfIcon />
                  </IconButton>
                  <IconButton
                    color="primary"
                    aria-label="upload kyc"
                    component="div"
                    className="image-preview-settings"
                    sx={{
                      position: "absolute !important",
                      top: "-20px !important",
                      right: "-10px",
                      color: "#ff001b !important",
                    }}
                    onClick={() => {
                      setData({ kyc_file: null, type: "" });
                      setUploading(false);
                    }}
                  >
                    <HighlightOffIcon
                      sx={{
                        fontSize: "2rem",
                      }}
                    />
                  </IconButton>
                </div>
              ) : (
                <div className="image-preview">
                  <div className="full-width">
                    <IconButton
                      color="primary"
                      aria-label="preview kyc"
                      component="span"
                      className="preview"
                      onClick={openModal}
                    >
                      <VisibilityIcon
                        sx={{
                          fontSize: "2rem",
                        }}
                      />
                    </IconButton>
                    <img
                      onClick={openModal}
                      className="full-width object-fit-cover cursor-pointer"
                      src={kyc_image}
                      alt="preview"
                    />
                    <IconButton
                      color="primary"
                      aria-label="upload kyc"
                      component="div"
                      className="image-preview-settings"
                      sx={{
                        top: "0 !important",
                        left: "unset !important",
                        color: "#ff001b !important",
                      }}
                      onClick={() => {
                        setData({ kyc_file: null, type: "" });
                        setUploading(false);
                      }}
                    >
                      <HighlightOffIcon
                        sx={{
                          fontSize: "2rem",
                        }}
                      />
                    </IconButton>
                  </div>
                </div>
              )
            ) : (
              <div
                className="drop-space"
                style={{
                  borderColor: isDragActive ? "red" : "#f5f5f5",
                  pointerEvents: uploading ? "progress" : "all",
                }}
                role="button"
                {...getRootProps()}
                tabindex="0"
              >
                {uploading ? (
                  <div
                    style={{
                      height: "15vh",
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress
                      sx={{
                        color: "#fff",
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <svg
                      className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-vubbuv"
                      focusable="false"
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      data-testid="CloudUploadIcon"
                    >
                      <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"></path>
                    </svg>
                    <p>Drag and drop your file here</p>
                    <p>or</p>
                    <button
                      className="btn btn-pill btn-white font-weight-bold opacity-90 px-15 py-3 just-white"
                      tabindex="0"
                      type="button"
                    >
                      Browse file
                      <span className="MuiTouchRipple-root css-w0pj6f"></span>
                    </button>
                    <input
                      {...getInputProps()}
                      accept={ACEPTED_FILE_TYPES.join()}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
